import { useState, useRef, useEffect } from 'react';

/**
 * Composant PatternLock - Grille 3x3 pour dessiner un pattern de déverrouillage
 * Comme sur les téléphones Android
 */
export default function PatternLockInput({ value, onChange, size = 200, readOnly = false }) {
  const canvasRef = useRef(null);
  const [pattern, setPattern] = useState(value ? value.split('-').map(Number) : []);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPos, setCurrentPos] = useState(null);

  // Mettre à jour le pattern quand value change (mode readOnly)
  useEffect(() => {
    if (value) {
      setPattern(value.split('-').map(Number));
    }
  }, [value]);

  // Positions des 9 points (grille 3x3)
  const dotSize = size / 5;
  const spacing = size / 3;
  
  const dots = [
    { id: 1, x: spacing * 0.5, y: spacing * 0.5 },
    { id: 2, x: spacing * 1.5, y: spacing * 0.5 },
    { id: 3, x: spacing * 2.5, y: spacing * 0.5 },
    { id: 4, x: spacing * 0.5, y: spacing * 1.5 },
    { id: 5, x: spacing * 1.5, y: spacing * 1.5 },
    { id: 6, x: spacing * 2.5, y: spacing * 1.5 },
    { id: 7, x: spacing * 0.5, y: spacing * 2.5 },
    { id: 8, x: spacing * 1.5, y: spacing * 2.5 },
    { id: 9, x: spacing * 2.5, y: spacing * 2.5 },
  ];

  // Dessiner le canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);

    // Fond
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, size, size);

    // Dessiner les lignes entre les points connectés
    if (pattern.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const firstDot = dots.find(d => d.id === pattern[0]);
      ctx.moveTo(firstDot.x, firstDot.y);

      for (let i = 1; i < pattern.length; i++) {
        const dot = dots.find(d => d.id === pattern[i]);
        ctx.lineTo(dot.x, dot.y);
      }

      // Ligne vers la position actuelle si on dessine
      if (isDrawing && currentPos) {
        ctx.lineTo(currentPos.x, currentPos.y);
      }

      ctx.stroke();
    } else if (pattern.length === 1 && isDrawing && currentPos) {
      // Première ligne en cours
      const firstDot = dots.find(d => d.id === pattern[0]);
      ctx.beginPath();
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 4;
      ctx.moveTo(firstDot.x, firstDot.y);
      ctx.lineTo(currentPos.x, currentPos.y);
      ctx.stroke();
    }

    // Dessiner les points
    dots.forEach((dot, index) => {
      const isSelected = pattern.includes(dot.id);
      const orderIndex = pattern.indexOf(dot.id);

      // Cercle extérieur
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, dotSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? '#10b981' : '#475569';
      ctx.fill();

      // Cercle intérieur
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, dotSize / 4, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? '#065f46' : '#1e293b';
      ctx.fill();

      // Numéro d'ordre si sélectionné
      if (isSelected) {
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${dotSize / 2.5}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((orderIndex + 1).toString(), dot.x, dot.y);
      }
    });

  }, [pattern, isDrawing, currentPos, size]);

  // Trouver le point le plus proche
  const findNearestDot = (x, y) => {
    const threshold = dotSize;
    for (const dot of dots) {
      const distance = Math.sqrt((x - dot.x) ** 2 + (y - dot.y) ** 2);
      if (distance < threshold && !pattern.includes(dot.id)) {
        return dot;
      }
    }
    return null;
  };

  // Obtenir les coordonnées relatives au canvas
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = size / rect.width;
    const scaleY = size / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleStart = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const coords = getCoords(e);
    const dot = findNearestDot(coords.x, coords.y);
    
    if (dot) {
      setPattern([dot.id]);
      setIsDrawing(true);
      setCurrentPos(coords);
    }
  };

  const handleMove = (e) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault();
    
    const coords = getCoords(e);
    setCurrentPos(coords);
    
    const dot = findNearestDot(coords.x, coords.y);
    if (dot && !pattern.includes(dot.id)) {
      setPattern(prev => [...prev, dot.id]);
    }
  };

  const handleEnd = () => {
    if (readOnly) return;
    setIsDrawing(false);
    setCurrentPos(null);
    
    if (pattern.length > 0) {
      const patternString = pattern.join('-');
      onChange(patternString);
    }
  };

  const handleClear = () => {
    if (readOnly) return;
    setPattern([]);
    onChange('');
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        className={`rounded-lg touch-none ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
        style={{ width: size, height: size }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />
      
      {!readOnly && (
        <>
          <div className="flex items-center gap-3">
            {pattern.length > 0 && (
              <div className="text-sm text-white/70">
                Pattern: <span className="font-mono text-emerald-400">{pattern.join(' → ')}</span>
              </div>
            )}
            <button
              type="button"
              onClick={handleClear}
              className="text-sm text-red-400 hover:text-red-300 underline"
            >
              Effacer
            </button>
          </div>
          
          {/* Légende */}
          <div className="text-xs text-white/50 text-center">
            Cliquez et glissez pour dessiner le schéma
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Composant pour afficher un pattern en lecture seule (pour l'impression)
 */
export function PatternDisplay({ pattern, size = 100 }) {
  const canvasRef = useRef(null);
  
  const patternArray = pattern ? pattern.split('-').map(Number) : [];
  
  const spacing = size / 3;
  const dotRadius = size / 15;
  
  const dots = [
    { id: 1, x: spacing * 0.5, y: spacing * 0.5 },
    { id: 2, x: spacing * 1.5, y: spacing * 0.5 },
    { id: 3, x: spacing * 2.5, y: spacing * 0.5 },
    { id: 4, x: spacing * 0.5, y: spacing * 1.5 },
    { id: 5, x: spacing * 1.5, y: spacing * 1.5 },
    { id: 6, x: spacing * 2.5, y: spacing * 1.5 },
    { id: 7, x: spacing * 0.5, y: spacing * 2.5 },
    { id: 8, x: spacing * 1.5, y: spacing * 2.5 },
    { id: 9, x: spacing * 2.5, y: spacing * 2.5 },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);

    // Fond blanc pour l'impression
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    // Dessiner les lignes
    if (patternArray.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;

      const firstDot = dots.find(d => d.id === patternArray[0]);
      ctx.moveTo(firstDot.x, firstDot.y);

      for (let i = 1; i < patternArray.length; i++) {
        const dot = dots.find(d => d.id === patternArray[i]);
        ctx.lineTo(dot.x, dot.y);
      }
      ctx.stroke();
    }

    // Dessiner les points
    dots.forEach(dot => {
      const isSelected = patternArray.includes(dot.id);
      const orderIndex = patternArray.indexOf(dot.id);

      ctx.beginPath();
      ctx.arc(dot.x, dot.y, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? '#000000' : '#cccccc';
      ctx.fill();

      if (isSelected) {
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${dotRadius * 1.5}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((orderIndex + 1).toString(), dot.x, dot.y);
      }
    });

  }, [pattern, size]);

  if (!pattern) return null;

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * Génère le SVG du pattern pour l'impression
 */
export function generatePatternSVG(pattern, size = 80) {
  if (!pattern) return '';
  
  const patternArray = pattern.split('-').map(Number);
  const spacing = size / 3;
  const dotRadius = size / 15;
  
  const dots = [
    { id: 1, x: spacing * 0.5, y: spacing * 0.5 },
    { id: 2, x: spacing * 1.5, y: spacing * 0.5 },
    { id: 3, x: spacing * 2.5, y: spacing * 0.5 },
    { id: 4, x: spacing * 0.5, y: spacing * 1.5 },
    { id: 5, x: spacing * 1.5, y: spacing * 1.5 },
    { id: 6, x: spacing * 2.5, y: spacing * 1.5 },
    { id: 7, x: spacing * 0.5, y: spacing * 2.5 },
    { id: 8, x: spacing * 1.5, y: spacing * 2.5 },
    { id: 9, x: spacing * 2.5, y: spacing * 2.5 },
  ];

  // Créer le chemin des lignes
  let pathD = '';
  if (patternArray.length > 0) {
    const firstDot = dots.find(d => d.id === patternArray[0]);
    pathD = `M ${firstDot.x} ${firstDot.y}`;
    for (let i = 1; i < patternArray.length; i++) {
      const dot = dots.find(d => d.id === patternArray[i]);
      pathD += ` L ${dot.x} ${dot.y}`;
    }
  }

  // Créer les cercles
  let circles = dots.map(dot => {
    const isSelected = patternArray.includes(dot.id);
    const orderIndex = patternArray.indexOf(dot.id);
    
    return `
      <circle cx="${dot.x}" cy="${dot.y}" r="${dotRadius}" fill="${isSelected ? '#000' : '#ccc'}"/>
      ${isSelected ? `<text x="${dot.x}" y="${dot.y}" text-anchor="middle" dy="0.35em" fill="#fff" font-size="${dotRadius * 1.5}" font-weight="bold">${orderIndex + 1}</text>` : ''}
    `;
  }).join('');

  return `
    <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
      <rect width="${size}" height="${size}" fill="#fff"/>
      ${pathD ? `<path d="${pathD}" stroke="#000" stroke-width="2" fill="none"/>` : ''}
      ${circles}
    </svg>
  `;
}
